import type { SupabaseClient } from "@supabase/supabase-js";

// Feed v1 (product lock 2026-08-07 §2.7-2.10, F4, D3; design draft 1 plates 04 and 05).
//
// Feed is a BULLETIN surface, not chat: a supervisor posts, the company reads, and some posts have
// to be acknowledged. One shared model so the console author view and the guard read view cannot
// disagree about ordering, about what "needs acknowledgement" means, or about the copy.
//
// SERVER: aligned to migration 20260807230000_feed_v1.sql (PR #204) public RPCs.

export const LIST_FEED_POSTS_RPC = "list_feed_posts";
export const GET_FEED_POST_RPC = "get_feed_post";
export const LIST_FEED_POST_ACKS_RPC = "list_feed_post_ack_status";
export const ACK_FEED_POST_RPC = "acknowledge_feed_post";
export const PUBLISH_FEED_POST_RPC = "create_feed_post";
export const SET_FEED_POST_PINNED_RPC = "update_feed_post_pin";
export const SET_FEED_POST_REQUIRE_ACK_RPC = "update_feed_post_require_ack";

/** Permission key gating authoring, pin, require-ack, and the ack roster. */
export const FEED_MANAGE_PERMISSION = "feed.manage";

/**
 * Product-facing audience labels. v1 server targeting is role/membership arrays with empty = all
 * active members. Console only offers "All company" until site/group targeting lands on the server.
 */
export const FEED_AUDIENCE_KINDS = ["account", "site", "shift_group"] as const;
export type FeedAudienceKind = (typeof FEED_AUDIENCE_KINDS)[number];

export interface FeedPost {
  readonly postId: string;
  /** Echo of the account the list was loaded for (list rows omit account_id). */
  readonly accountId: string;
  readonly title: string;
  readonly body: string;
  readonly audienceKind: FeedAudienceKind;
  readonly audienceLabel: string;
  readonly authorDisplayName: string;
  readonly isPinned: boolean;
  readonly requiresAck: boolean;
  readonly ackDueAt: string | null;
  readonly publishedAt: string | null;
  /** The reading member's own acknowledgement. Null means outstanding. */
  readonly ackedAt: string | null;
  readonly ackCount: number;
  readonly channelEventKey: string | null;
}

export interface FeedAckRosterEntry {
  readonly memberDisplayName: string;
  readonly role: string;
  readonly ackedAt: string | null;
  readonly currentlyTargeted: boolean;
}

const FEED_PAGE_LIMIT = 50;

export const FEED_UNAVAILABLE =
  "Feed is not available on this server yet. Nothing has been posted or missed.";

export const FEED_LOAD_FAILED = "Could not load the Feed. Check your connection and try again.";

export type FeedLoad =
  | { readonly status: "ready"; readonly posts: readonly FeedPost[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

// Codes meaning "this RPC is not deployed here", as opposed to "the call failed".
const MISSING_SURFACE_CODES: ReadonlySet<string> = new Set(["42883", "42P01", "PGRST202", "PGRST205"]);

export function isMissingFeedSurface(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && MISSING_SURFACE_CODES.has(code)) return true;
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && message.toLowerCase().includes("could not find the function");
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asAudienceKind(value: unknown): FeedAudienceKind {
  return typeof value === "string" && (FEED_AUDIENCE_KINDS as readonly string[]).includes(value)
    ? (value as FeedAudienceKind)
    : "account";
}

function asBool(value: unknown, ...aliases: unknown[]): boolean {
  if (value === true) return true;
  for (const alias of aliases) {
    if (alias === true) return true;
  }
  return false;
}

/** Parse one server row. Returns null when the identifying fields are missing - never a guess. */
export function mapFeedPost(raw: unknown, accountId = ""): FeedPost | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const postId = asText(row.post_id);
  if (postId === null) return null;
  // #204 list/get returns pinned / require_ack / acknowledged_at (not the client-proposed aliases).
  const count = Number(row.ack_count ?? row.acknowledged_target_count);
  return {
    postId,
    accountId: asText(row.account_id) ?? accountId,
    title: asText(row.title) ?? "Untitled post",
    body: asText(row.body) ?? "",
    audienceKind: asAudienceKind(row.audience_kind),
    audienceLabel: asText(row.audience_label) ?? "All company",
    authorDisplayName: asText(row.author_display_name) ?? "SecOps",
    isPinned: asBool(row.pinned, row.is_pinned),
    requiresAck: asBool(row.require_ack, row.requires_ack),
    ackDueAt: asText(row.ack_due_at),
    publishedAt: asText(row.published_at),
    ackedAt: asText(row.acknowledged_at) ?? asText(row.acked_at),
    ackCount: Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0,
    channelEventKey: asText(row.channel_event_key),
  };
}

export function mapFeedPosts(raw: unknown, accountId = ""): FeedPost[] {
  if (!Array.isArray(raw)) return [];
  const posts: FeedPost[] = [];
  for (const row of raw) {
    const post = mapFeedPost(row, accountId);
    if (post !== null) posts.push(post);
  }
  return sortFeedPosts(posts);
}

export function mapFeedAckRoster(raw: unknown): FeedAckRosterEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: FeedAckRosterEntry[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const name = asText(row.member_display_name);
    if (name === null) continue;
    entries.push({
      memberDisplayName: name,
      role: asText(row.role) ?? "guard",
      ackedAt: asText(row.acknowledged_at) ?? asText(row.acked_at),
      currentlyTargeted: row.currently_targeted === true,
    });
  }
  // Outstanding first: the roster exists to answer "who still has not read this".
  return entries.sort((left, right) => {
    if ((left.ackedAt === null) !== (right.ackedAt === null)) return left.ackedAt === null ? -1 : 1;
    return left.memberDisplayName.localeCompare(right.memberDisplayName);
  });
}

/**
 * Plate 04's order: pinned first, then posts still needing this reader's acknowledgement, then
 * reverse chronological.
 */
export function sortFeedPosts(posts: readonly FeedPost[]): FeedPost[] {
  return [...posts].sort((left, right) => {
    if (left.isPinned !== right.isPinned) return left.isPinned ? -1 : 1;
    const leftOutstanding = needsAcknowledgement(left);
    const rightOutstanding = needsAcknowledgement(right);
    if (leftOutstanding !== rightOutstanding) return leftOutstanding ? -1 : 1;
    return publishedTime(right) - publishedTime(left);
  });
}

function publishedTime(post: FeedPost): number {
  const parsed = Date.parse(post.publishedAt ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

/** A post this reader must still acknowledge. */
export function needsAcknowledgement(post: FeedPost): boolean {
  return post.requiresAck && post.ackedAt === null;
}

export function outstandingAckCount(posts: readonly FeedPost[]): number {
  return posts.filter(needsAcknowledgement).length;
}

/** Plate 04's soft strip above the fold. Null when nothing is outstanding. */
export function outstandingAckStrip(posts: readonly FeedPost[]): string | null {
  const count = outstandingAckCount(posts);
  if (count === 0) return null;
  return count === 1 ? "1 post needs your acknowledgement" : `${count} posts need your acknowledgement`;
}

export type FeedAckAffordance =
  | { readonly kind: "row"; readonly label: string }
  | { readonly kind: "detail-only"; readonly label: string }
  | { readonly kind: "done"; readonly label: string };

/**
 * Hybrid ack rule (lock D3): required acks only from the opened post; optional may from the list.
 */
export function feedAckAffordance(post: FeedPost, surface: "list" | "detail"): FeedAckAffordance {
  if (post.ackedAt !== null) {
    return { kind: "done", label: "Acknowledged" };
  }
  if (!post.requiresAck) {
    return { kind: "row", label: "Acknowledge" };
  }
  return surface === "detail"
    ? { kind: "row", label: "Acknowledge" }
    : { kind: "detail-only", label: "Open to acknowledge" };
}

export const FEED_ACK_CONSENT_LINE = "Records your name and the time.";

export function feedAckFootnote(post: FeedPost, formatDue: (iso: string) => string | null): string {
  const due = post.ackDueAt === null ? null : formatDue(post.ackDueAt);
  return due === null ? FEED_ACK_CONSENT_LINE : `${FEED_ACK_CONSENT_LINE} Due ${due}.`;
}

// --- reads -------------------------------------------------------------------

export async function fetchFeedPosts(client: SupabaseClient, accountId: string): Promise<FeedLoad> {
  try {
    const { data, error } = await client.rpc(LIST_FEED_POSTS_RPC, {
      p_account_id: accountId,
      p_limit: FEED_PAGE_LIMIT,
    });
    if (error === null) {
      return { status: "ready", posts: mapFeedPosts(data, accountId) };
    }
    if (isMissingFeedSurface(error)) {
      return { status: "unavailable", reason: FEED_UNAVAILABLE };
    }
    // Prefer host exception text when it is a known membership/permission sentence so
    // switcher-without-membership is not misread as an offline/network problem.
    const hostMessage = (error as { message?: unknown }).message;
    if (typeof hostMessage === "string" && KNOWN_FEED_FAILURES.has(hostMessage)) {
      return { status: "failed", reason: hostMessage };
    }
    return { status: "failed", reason: FEED_LOAD_FAILED };
  } catch {
    return { status: "failed", reason: FEED_LOAD_FAILED };
  }
}

/** Single post for the detail screen. Throws safe so read-model hooks can show an error state. */
export async function fetchFeedPost(
  client: SupabaseClient,
  accountId: string,
  postId: string,
): Promise<FeedPost | null> {
  const { data, error } = await client.rpc(GET_FEED_POST_RPC, {
    p_account_id: accountId,
    p_post_id: postId,
  });
  if (error !== null) {
    throw new Error("Could not load that post.");
  }
  return mapFeedPost(Array.isArray(data) ? data[0] : data, accountId);
}

export async function fetchFeedAckRoster(
  client: SupabaseClient,
  accountId: string,
  postId: string,
): Promise<FeedAckRosterEntry[]> {
  const { data, error } = await client.rpc(LIST_FEED_POST_ACKS_RPC, {
    p_account_id: accountId,
    p_post_id: postId,
  });
  if (error !== null) {
    throw new Error("Could not load who has acknowledged this post.");
  }
  return mapFeedAckRoster(data);
}

// --- writes ------------------------------------------------------------------

export const SAFE_FEED_FAILURE = "Couldn't complete that Feed action - please try again.";

/**
 * Verbatim refusal sentences from 20260807230000_feed_v1.sql write/read paths.
 * Anything else collapses to SAFE_FEED_FAILURE.
 */
export const KNOWN_FEED_FAILURES: ReadonlySet<string> = new Set([
  "Feed publish rate limit exceeded. Try again later.",
  "Feed acknowledgement rate limit exceeded. Try again later.",
  "Authentication is required to read Feed posts.",
  "Authentication is required to manage Feed posts.",
  "Authentication is required to audit Feed posts.",
  "Account id is required to read Feed posts.",
  "Account id is required to manage Feed posts.",
  "Account id is required to audit Feed posts.",
  "Active account membership is required to list Feed posts.",
  "Active account membership is required to read Feed posts.",
  "Active account membership is required to manage Feed posts.",
  "Active account membership is required to audit Feed posts.",
  "Join this account as admin or start a support session to manage Feed posts. Platform superadmin alone cannot author or acknowledge Feed rows without a membership.",
  "Feed list limit must be between 1 and 100.",
  "Feed cursor was not found or is not visible.",
  "Feed post id is required.",
  "Feed post was not found or is not visible.",
  "Feed management permission is required.",
  "Feed post title must be between 1 and 160 characters.",
  "Feed post body must be between 1 and 20000 characters.",
  "Feed post pin and acknowledgement settings are required.",
  "Feed audience roles cannot contain more than 4 entries.",
  "Feed audience memberships cannot contain more than 1000 entries.",
  "Feed audience targets cannot contain null values.",
  "Feed audience memberships must be active members of this account.",
  "Feed post was not found.",
  "Feed post is archived.",
  "Feed post pin setting is required.",
  "Feed post acknowledgement setting is required.",
  // Client-side (before network)
  "Site and group audiences are not available yet. Post to All company.",
]);

export function mapFeedFailure(message: unknown): string {
  return typeof message === "string" && KNOWN_FEED_FAILURES.has(message) ? message : SAFE_FEED_FAILURE;
}

export type FeedWriteOutcome = { readonly ok: true } | { readonly ok: false; readonly reason: string };

async function runFeedWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<FeedWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    return error === null ? { ok: true } : { ok: false, reason: mapFeedFailure((error as { message?: unknown }).message) };
  } catch {
    return { ok: false, reason: SAFE_FEED_FAILURE };
  }
}

/**
 * Acknowledge as the signed-in member. Identity comes from auth.uid() on the server.
 */
export async function acknowledgeFeedPost(
  client: SupabaseClient,
  accountId: string,
  postId: string,
): Promise<FeedWriteOutcome> {
  const trimmed = postId.trim();
  if (accountId.trim().length === 0 || trimmed.length === 0) {
    return { ok: false, reason: SAFE_FEED_FAILURE };
  }
  return runFeedWrite(client, ACK_FEED_POST_RPC, {
    p_account_id: accountId,
    p_post_id: trimmed,
  });
}

export interface FeedPostDraft {
  readonly title: string;
  readonly body: string;
  readonly audienceKind: FeedAudienceKind;
  readonly audienceId: string | null;
  readonly isPinned: boolean;
  readonly requiresAck: boolean;
  readonly ackDueAt: string | null;
}

export const EMPTY_FEED_POST_DRAFT: FeedPostDraft = {
  title: "",
  body: "",
  audienceKind: "account",
  audienceId: null,
  isPinned: false,
  requiresAck: false,
  ackDueAt: null,
};

/** Pre-network checks. Reasons name the field, never echo the value. */
export function validateFeedPostDraft(draft: FeedPostDraft): { ok: true } | { ok: false; reason: string } {
  if (draft.title.trim().length === 0) return { ok: false, reason: "Give the post a title." };
  if (draft.body.trim().length === 0) return { ok: false, reason: "Write the post body." };
  // Server v1 is all-members or role/membership arrays; site/shift_group targets are not wired yet.
  if (draft.audienceKind !== "account") {
    return {
      ok: false,
      reason: "Site and group audiences are not available yet. Post to All company.",
    };
  }
  return { ok: true };
}

export async function publishFeedPost(
  client: SupabaseClient,
  accountId: string,
  draft: FeedPostDraft,
): Promise<FeedWriteOutcome> {
  const validated = validateFeedPostDraft(draft);
  if (!validated.ok) return validated;

  // Empty audience arrays mean all active memberships (Codex PR #204 body).
  return runFeedWrite(client, PUBLISH_FEED_POST_RPC, {
    p_account_id: accountId,
    p_title: draft.title.trim(),
    p_body: draft.body.trim(),
    p_pinned: draft.isPinned,
    p_require_ack: draft.requiresAck,
    p_audience_roles: null,
    p_audience_membership_ids: null,
  });
}

export async function setFeedPostPinned(
  client: SupabaseClient,
  accountId: string,
  postId: string,
  isPinned: boolean,
): Promise<FeedWriteOutcome> {
  return runFeedWrite(client, SET_FEED_POST_PINNED_RPC, {
    p_account_id: accountId,
    p_post_id: postId,
    p_pinned: isPinned,
  });
}

export async function setFeedPostRequireAck(
  client: SupabaseClient,
  accountId: string,
  postId: string,
  requiresAck: boolean,
): Promise<FeedWriteOutcome> {
  return runFeedWrite(client, SET_FEED_POST_REQUIRE_ACK_RPC, {
    p_account_id: accountId,
    p_post_id: postId,
    p_require_ack: requiresAck,
  });
}
